import { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Checkbox,
    Box,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { ActionButton, CancelButton, SubmitButton } from 'components/ui/buttons';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import { useAuth } from 'context/auth';

export default function OrderProducts({ eventId, tranId, onEdit, canEdit = false }) {
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [tickets, setTickets] = useState([]);
    const [products, setProducts] = useState([]);
    const [editableTickets, setEditableTickets] = useState([]);
    const [addedTickets, setAddedTickets] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [refresh, setRefresh] = useState(0);

    const { auth } = useAuth();
    const { notify } = useNotification();

    useEffect(() => {
        if (!eventId || !tranId) return;

        setStatus('loading');

        Promise.all([getOrderProducts(eventId, tranId), getSelectableProducts(eventId)])
            .then(([orderTickets, selectableProducts]) => {
                setTickets(orderTickets);
                setProducts(selectableProducts);
                setEditableTickets(createEditableTickets(orderTickets));
                setAddedTickets([]);
                setIsEditing(false);
                setIsSaveConfirmationOpen(false);
                setStatus('success');
            })
            .catch(() => {
                setStatus('error');
            });
    }, [eventId, tranId, refresh]);

    const resetDraftState = () => {
        setEditableTickets(createEditableTickets(tickets));
        setAddedTickets([]);
    };

    const handleEditStart = () => {
        resetDraftState();
        setIsEditing(true);
    };

    const handleCancel = () => {
        resetDraftState();
        setIsEditing(false);
        setIsSaveConfirmationOpen(false);
    };

    const handleVoidToggle = (ticketIndex) => {
        setEditableTickets((current) =>
            current.map((ticket, index) =>
                index === ticketIndex
                    ? { ...ticket, markedForVoid: !ticket.markedForVoid }
                    : ticket,
            ),
        );
    };

    const handleAddTicket = (productId) => {
        if (!productId) return;

        const product = products.find((item) => String(item.product_id) === String(productId));

        if (!product) return;
        setAddedTickets((current) => [
            ...current,
            {
                draftId: createDraftId(),
                product_id: product.product_id,
                prod_name: product.prod_name,
                prod_type_id: product.prod_type_id,
                is_attendee: product.is_attendee,
            },
        ]);
    };

    const handleRemoveAddedTicket = (draftId) => {
        setAddedTickets((current) => current.filter((ticket) => ticket.draftId !== draftId));
    };

    const getSavePayload = () => ({
        voidedProducts: editableTickets
            .filter((ticket) => ticket.markedForVoid)
            .map((ticket) => ({ unipass_id: ticket.unipass_id })),
        addedProducts: addedTickets.map((ticket) => ({ product_id: ticket.product_id })),
    });

    const handleSaveChanges = () => {
        setIsSaveConfirmationOpen(true);
    };

    const handleCancelSaveConfirmation = () => {
        setIsSaveConfirmationOpen(false);
    };

    const handleConfirmSaveChanges = async () => {
        if (isSaving) {
            return;
        }

        setIsSaving(true);
        const payload = getSavePayload();

        try {
            const response = await fetchExpresso(
                `/apiv2/events/${eventId}/orders/${tranId}/products/`,
                {
                    method: 'PUT',
                    body: payload,
                },
            );

            if (response.status === 200) {
                notify.success('Order Updated');
                setIsEditing(false);
                setIsSaveConfirmationOpen(false);
                setRefresh((current) => current + 1);
                if (onEdit) {
                    onEdit();
                }
            } else {
                notify.error("We're experiencing a temporary issue. Please try again later.");
            }
        } catch (e) {
            notify.error('Unable to update order. Please check your connection and try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const unsavedChanges = addedTickets.length > 0 || editableTickets.some((t) => t.markedForVoid);
    const pendingVoidedTickets = editableTickets.filter((ticket) => ticket.markedForVoid);
    const cardSubheader = isEditing
        ? 'Select tickets to void, or add new tickets to this order.'
        : 'View or edit the tickets included in this order.';

    return (
        <Card>
            <CardHeader title="Tickets in This Order" subheader={cardSubheader} />
            <CardContent>
                {status === 'loading' && (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {status === 'error' && (
                    <Typography color="error" variant="body2">
                        Unable to load tickets for this order.
                    </Typography>
                )}
                {status === 'success' && isEditing === true && (
                    <>
                        <VoidTable tickets={editableTickets} onVoidToggle={handleVoidToggle} />
                        <AddTable
                            addedTickets={addedTickets}
                            products={products}
                            onAddTicket={handleAddTicket}
                            onRemoveAddedTicket={handleRemoveAddedTicket}
                        />
                    </>
                )}
                {status === 'success' && isEditing === false && <TicketTable tickets={tickets} />}
            </CardContent>
            {auth.admin && canEdit ? (
                <StyledCardActions>
                    {isEditing === true && (
                        <CancelButton disabled={status !== 'success'} onClick={handleCancel}>
                            Go Back
                        </CancelButton>
                    )}
                    {isEditing === true && (
                        <SubmitButton
                            disabled={status !== 'success' || !unsavedChanges}
                            onClick={handleSaveChanges}
                        >
                            Review Changes
                        </SubmitButton>
                    )}
                    {isEditing === false && (
                        <ActionButton disabled={status !== 'success'} onClick={handleEditStart}>
                            Edit Order Tickets
                        </ActionButton>
                    )}
                </StyledCardActions>
            ) : null}
            <EditConfirmationDialog
                open={isSaveConfirmationOpen}
                voidedTickets={pendingVoidedTickets}
                addedTickets={addedTickets}
                saving={isSaving}
                onCancel={handleCancelSaveConfirmation}
                onConfirm={handleConfirmSaveChanges}
            />
        </Card>
    );
}

function TicketTable({ tickets }) {
    if (!tickets.length) {
        return <Typography variant="body2">No tickets found for this order.</Typography>;
    }

    return (
        <TableBox>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width={20}>Ticket #</TableCell>
                        <TableCell>Ticket Type</TableCell>
                        <TableCell align='right' width={70}></TableCell>
                        <TableCell align='right' width={70}>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tickets.map((ticket, index) => (
                        <TicketRow key={ticket.unipass_id ?? index} disabled={ticket.is_void === 1}>
                            <TableCell>
                                {'#' + String(ticket.unipass_number).padStart(3, '0')}
                            </TableCell>
                            <TableCell>{ticket.prod_name}</TableCell>
                            <TableCell align='right'>
                                {ticket.is_attendee === 1 ? 'attendee' : ''}
                            </TableCell>
                            <TableCell align='right'>
                                <StatusChip ticket={ticket} />
                            </TableCell>
                        </TicketRow>
                    ))}
                </TableBody>
            </Table>
        </TableBox>
    );
}

function VoidTable({ tickets, onVoidToggle }) {
    return (
        <>
            <TableBox>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={20}>Ticket #</TableCell>
                            <TableCell>Ticket Type</TableCell>
                            <TableCell align="right" width={70}></TableCell>
                            <TableCell align="right" width={70}>Void</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.length > 0 ? (
                            tickets.map((ticket, index) => (
                                <TicketRow
                                    key={ticket.unipass_id ?? `${ticket.unipass_number}-${index}`}
                                    disabled={ticket.is_void === 1}
                                >
                                    <TableCell>
                                        {'#' + String(ticket.unipass_number).padStart(3, '0')}
                                    </TableCell>
                                    <TableCell>{ticket.prod_name}</TableCell>
                                    <TableCell align="right">
                                        {ticket.is_attendee === 1 ? 'attendee' : ''}
                                    </TableCell>
                                    <TableCell align="right" padding="checkbox">
                                        {ticket.is_void ? (
                                            <StatusChip ticket={ticket} />
                                        ) : (
                                            <Checkbox
                                                checked={ticket.markedForVoid}
                                                onChange={() => onVoidToggle(index)}
                                            />
                                        )}
                                    </TableCell>
                                </TicketRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2">
                                        No tickets found for this order.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableBox>
            <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography variant="caption">
                    Check any tickets that should be voided from this order.
                </Typography>
            </Box>
        </>
    );
}

export function AddTable({ addedTickets, products, onAddTicket, onRemoveAddedTicket }) {
    const [productFilter, setProductFilter] = useState('');

    const handleProductSelect = (_, product) => {
        if (product) {
            onAddTicket(product.product_id);
        }
    };

    return (
        <Box mt={3}>
            <Box mb={1}>
                <Typography variant="subtitle2">Add a Ticket</Typography>
            </Box>
            <TableBox>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Ticket Type</TableCell>
                            <TableCell width={70} align="right"></TableCell>
                            <TableCell width={70} align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {addedTickets.map((ticket) => (
                            <TableRow key={ticket.draftId}>
                                <TableCell>{ticket.prod_name}</TableCell>
                                <TableCell align="right">
                                    {ticket.is_attendee === 1 && 'attendee'}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        onClick={() => onRemoveAddedTicket(ticket.draftId)}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Autocomplete
                                    options={products}
                                    value={null}
                                    inputValue={productFilter}
                                    getOptionLabel={(product) => product.prod_name}
                                    isOptionEqualToValue={(option, value) =>
                                        option.product_id === value.product_id
                                    }
                                    onChange={handleProductSelect}
                                    onInputChange={(_, newInputValue, reason) => {
                                        if (reason === 'input' || reason === 'clear') {
                                            setProductFilter(newInputValue);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select a ticket type"
                                            size="small"
                                        />
                                    )}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableBox>
        </Box>
    );
}

function EditConfirmationDialog({
    open,
    voidedTickets,
    addedTickets,
    saving,
    onCancel,
    onConfirm,
}) {
    const rows = [
        ...voidedTickets.map((ticket) => ({
            key: `void-${ticket.unipass_id}`,
            action: 'voided',
            ticket,
            attendee: ticket.is_attendee === 1,
        })),
        ...addedTickets.map((ticket) => ({
            key: `new-${ticket.draftId}`,
            action: 'added',
            ticket,
            attendee: ticket.is_attendee === 1,
        })),
    ];

    const hasNewAttendeeTickets = addedTickets.some((ticket) => ticket.is_attendee === 1);
    const hasVoidedAttendeeTickets = voidedTickets.some((ticket) => ticket.is_attendee === 1);

    const alertMessages = [
        'Remember to resend tickets after saving',
        ...(hasVoidedAttendeeTickets ? ['You are voiding attendee tickets'] : []),
        ...(hasNewAttendeeTickets ? ['New attendees need their details filled in manually'] : []),
    ];

    return (
        <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="sm">
            <DialogTitle>Confirm Ticket Changes</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {alertMessages.map((message) => (
                            <li key={message}>{message}</li>
                        ))}
                    </Box>
                </Alert>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={120}>Change</TableCell>
                            <TableCell>Ticket Type</TableCell>
                            <TableCell width={100}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ key, action, ticket, attendee }) => (
                            <TableRow key={key}>
                                <TableCell>
                                    <ChangeTypeChip
                                        label={
                                            action === 'voided'
                                                ? 'Will be voided'
                                                : 'Will be added'
                                        }
                                        $action={action}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {ticket.unipass_number
                                        ? `#${String(ticket.unipass_number).padStart(3, '0')} `
                                        : ''}
                                    {ticket.prod_name}
                                </TableCell>
                                <TableCell>
                                    {attendee ? 'attendee' : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!rows.length && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2">
                                        No ticket changes to review.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={saving} onClick={onCancel}>
                    Go Back
                </CancelButton>
                <SubmitButton disabled={saving} onClick={onConfirm}>
                    {saving ? 'Saving...' : 'Confirm Changes'}
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function StatusChip({ ticket }) {
    let status = '';

    if (ticket.post_order_flag === 1) {
        status = 'added';
    }

    if (ticket.is_void === 1) {
        status = 'voided';
    }

    if (status === '') {
        return null;
    }

    return <ChangeTypeChip label={status === 'added' ? 'Added' : 'Voided'} $action={status} size="small" />;
}

// ***** UI Components ***** //

const StyledCardActions = styled(CardActions)(({ theme }) => ({
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
}));

const TicketRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    ...(disabled && {
        backgroundColor: theme.palette.grey[100],
        '& td': {
            color: theme.palette.text.secondary,
        },
    }),
}));

const TableBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

const ChangeTypeChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== '$action',
})(({ theme, $action }) => ({
    fontWeight: 600,
    ...($action === 'added' && {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
    }),
    ...($action === 'voided' && {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    }),
}));

// ***** API Calls ***** //

async function getOrderProducts(eventId, orderId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/products/`);

    if (response.status === 200) {
        const results = await response.json();
        return results.tickets || [];
    }

    throw new Error(`[${response.status}] Something went wrong`);
}

async function getSelectableProducts(eventId) {
    const response = await fetchExpresso(`/apiv2/events/${eventId}/products`);

    if (response.status === 200) {
        const results = await response.json();

        return results.filter((product) => product.prod_type_id === 1 || product.prod_type_id === 2);
    }

    throw new Error(`[${response.status}] Something went wrong`);
}

// ***** Utilities ***** //

function createEditableTickets(tickets) {
    return tickets.map((ticket) => ({
        ...ticket,
        markedForVoid: false,
    }));
}

function createDraftId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
